/**
 * The heart on the kid topic picker (kid play design §2, §7.6), apart from
 * React and Relay so `node --test` can reach it.
 */
import type { KidTopic } from '../views/topics.english.ts';
import type { StickerSlug } from './stickers.ts';

/**
 * `tutor_core::scrapbook::sticker_for_session`'s arms for the kid topics,
 * read backwards: the server stores a sticker, not a topic, so the favourite
 * can only be found by inverting the map it used. `//client:scrapbook_contract_test`
 * holds each entry to the server's arm, so the two cannot drift apart.
 */
export const KID_TOPIC_STICKERS: Record<KidTopic, StickerSlug> = {
  Animals: 'animals',
  Space: 'space',
  Food: 'food',
  Games: 'games',
  Cartoons: 'cartoons',
  Sports: 'sports',
  Music: 'music',
  'Toys and games': 'toys',
  School: 'school',
  Colours: 'colours',
};

/** §2: a habit, not the topic of the last talk or two. */
const WINDOW = 10;
const MIN_OCCURRENCES = 2;

/**
 * The tile whose sticker is most frequent among the newest ten pages, or
 * none. `pages` is newest first, as `Learner.scrapbook` answers, so a tie
 * goes to the sticker seen first. `chat` and `book` belong to no tile, and
 * neither does any other sticker a tile does not store (`weather`): each is
 * passed over for the next most frequent (§7.6). The game sessions' `games`
 * reaches the Games tile through its own arm.
 */
export function favouriteTopic(pages: readonly { sticker: string }[], tiles: readonly string[]): string | null {
  const tileOf = new Map<string, string>();
  for (const tile of tiles) {
    if (Object.hasOwn(KID_TOPIC_STICKERS, tile)) tileOf.set(KID_TOPIC_STICKERS[tile as KidTopic], tile);
  }

  // Insertion order is first sighting, so the stable sort keeps recency as the tiebreak.
  const counts = new Map<string, number>();
  for (const { sticker } of pages.slice(0, WINDOW)) counts.set(sticker, (counts.get(sticker) ?? 0) + 1);
  const ranked = [...counts].sort(([, a], [, b]) => b - a);

  for (const [sticker, count] of ranked) {
    if (count < MIN_OCCURRENCES) return null;
    const tile = tileOf.get(sticker);
    if (tile) return tile;
  }
  return null;
}
